import { useEffect, useState } from "react";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  CheckCircle2,
  User,
  MapPin,
  Phone,
  Mail,
  GraduationCap,
  Calendar,
} from "lucide-react";
import { supabase } from "../../integrations/supabase/client";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

export default function StudentRegistrationSuccess() {
  const navigate = usePrefixedNavigate();
  const [searchParams] = useSearchParams();
  const [studentData, setStudentData] = useState<any>(null);
  const studentId = searchParams.get("studentId");

  useEffect(() => {
    const fetchStudentData = async () => {
      if (studentId) {
        const { data } = await supabase
          .from("students")
          .select("*")
          .eq("id", studentId)
          .single();

        if (data) {
          setStudentData(data);
        }
      }
    };

    fetchStudentData();
  }, [studentId]);

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-background to-muted">
      <Header />
      <main className="flex-1 px-4 py-12">
        <div className="mx-auto max-w-4xl">
          {/* Success Header */}
          <div className="mb-8 text-center">
            <div className="mb-4 inline-flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-maroon mb-2 text-3xl font-bold">
              Student Registration Completed!
            </h1>
            <p className="text-muted-foreground">
              Your profile has been successfully created and you can now apply
              to hostels.
            </p>
          </div>

          {/* Student Details Card */}
          {studentData && (
            <Card className="border-maroon/20 mb-6 border-2">
              <CardContent className="p-6">
                <h2 className="text-maroon mb-6 text-xl font-semibold">
                  Student Profile
                </h2>

                <div className="grid gap-6 md:grid-cols-2">
                  {/* Personal Info */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <User className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Full Name
                        </p>
                        <p className="font-medium">
                          {studentData.first_name} {studentData.middle_name}{" "}
                          {studentData.last_name}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Calendar className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Date of Birth
                        </p>
                        <p className="font-medium">{studentData.dob}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MapPin className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Location
                        </p>
                        <p className="font-medium">
                          {studentData.city}, {studentData.state}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Contact</p>
                        <p className="font-medium">{studentData.mobile}</p>
                      </div>
                    </div>
                  </div>

                  {/* Academic Info */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Mail className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium">{studentData.email}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <GraduationCap className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Current Class
                        </p>
                        <p className="font-medium">
                          {studentData.current_class}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <GraduationCap className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Medium</p>
                        <p className="font-medium capitalize">
                          {studentData.medium}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="text-maroon mt-1 h-5 w-5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <p className="font-medium capitalize">
                          <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm text-green-700">
                            Active
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preferred Cities */}
                {studentData.preferred_cities &&
                  studentData.preferred_cities.length > 0 && (
                    <div className="mt-6 border-t pt-6">
                      <p className="mb-3 text-sm text-muted-foreground">
                        Preferred Cities
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {studentData.preferred_cities.map((city: string) => (
                          <span
                            key={city}
                            className="bg-maroon/10 text-maroon rounded-full px-3 py-1 text-sm"
                          >
                            {city}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          )}

          {/* Next Steps */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">What&apos;s Next?</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-maroon font-bold">1.</span>
                  <span>
                    Browse available hostels that match your preferences
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-maroon font-bold">2.</span>
                  <span>Submit applications to hostels of your choice</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-maroon font-bold">3.</span>
                  <span>
                    Track your applications and schedule interviews from your
                    dashboard
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              onClick={() => navigate("/student/dashboard")}
              className="bg-maroon hover:bg-maroon/90 px-8 text-white"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="border-maroon text-maroon hover:bg-maroon/10"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
